/**
 * @biomax/plugin-surveillance-feed — live surveillance-signal layer for PaCC.
 *
 * The full PaCC product must run on live, real, non-stale case data (no
 * fake data, no outdated data). No single global API of doctor-reported
 * cases exists (sovereignty + privacy + inherent lag), so the production
 * feed is a *federated patchwork* of sources fused into one stream. See
 * designs/biomax-epi/pacc-data-sourcing.md.
 *
 * This plugin defines:
 *   - the unified `Signal` schema (the live-data analogue of a catalog record)
 *   - `validateSignal` (verifier-first: every signal needs source + asOf + provenance)
 *   - `fuseSignals` (dedup multi-source reports; promote-on-corroboration)
 *   - freshness helpers (per-source cadence + staleness)
 *   - an in-memory injectable store (__setSignalRecords / listSignals / …)
 *
 * Adapter *normalisers* (raw payload → Signal[]) live in ./adapters and are
 * pure + offline-testable; only the fetch wrapper touches the network.
 *
 * kingdoms: '*'.
 */

import type { PluginManifest } from '@biomax/types';
import type { SourceReport } from './ingest.js';

// ---------------------------------------------------------------------------
// Signal schema
// ---------------------------------------------------------------------------

export type SignalMetric =
  | 'cases' | 'deaths' | 'hospitalisations' | 'rna-copies' | 'seq-frequency'
  | 'wastewater-activity'   // viral-activity level relative to a site's own baseline (source scale, e.g. CDC NWSS; can exceed 100 during surges — NOT a strict 0-100 percentile)
  | 'event-notification'    // an official outbreak event was reported here (e.g. WHO DON) — value is event-count, NOT a case count. Counts require a count-carrying source.
  | 'sequences'             // count of pathogen genomes collected here in a recent window (genomic-surveillance SAMPLING effort — NOT incidence; many infections are never sequenced). value = sequence count.
  | 'test-positivity'       // % of specimens testing positive (0-100). A transmission-intensity indicator, NOT a case count.
  | 'syndromic-rate'        // syndromic indicator: % of clinical visits for an illness (ILI / ED-visit %, 0-100). A leading signal, NOT a case count.
  | 'hospitalisation-rate'; // new admissions per 100,000 population (already per-capita — must NOT be re-divided by the per-100k view).

/** Confidence tier, ordered weakest → strongest. */
export type Confidence =
  | 'syndromic' | 'automated-event' | 'moderated-event' | 'national-dashboard' | 'official';

export type VerificationStatus = 'unverified' | 'probable' | 'confirmed';

export interface SignalLocation {
  lat: number;
  lon: number;
  /** Free-text admin label (city/region/country). */
  admin: string;
  iso3?: string;
}

export interface SignalProvenance {
  url?: string;
  /** ISO timestamp the datum was retrieved by the adapter. */
  retrievedAt: string;
  /** Optional verbatim quote from the source that establishes the numeric
   *  value. Phase 4 governance rail: when a downstream consumer (analyst,
   *  briefing recipient, regulator) asks "where did this 443 come from?",
   *  we want to surface the source's own words — not just a URL. Adapters
   *  populate this opportunistically; not every source can be quoted (CSV /
   *  JSON rows don't have prose), but PDF-extracted surveillance bulletins
   *  + WHO DON narratives + ProMED moderator notes are natural fits.
   *
   *  Pattern: ≤300 chars verbatim, no editing for clarity. If the source
   *  prose says "37,388 cases of ARI pneumonia and 65 associated deaths
   *  were reported", that's the quote — preserve the units and the
   *  inclusion language. Surfaces render it monospace + with the source
   *  URL alongside. */
  verifierQuote?: {
    /** Exact verbatim text. ≤300 chars; truncate with ellipsis if longer. */
    text: string;
    /** Optional page/section anchor for PDF sources. */
    pageOrSection?: string;
  };
}

/**
 * A losing source's numeric report for the SAME event/place/metric whose value
 * DISAGREES with the chosen primary. Retained on the fused signal so a conflict
 * is never silently overwritten — the primary count is the highest-confidence /
 * most-recent source's, alternates stay visible + traceable.
 */
export interface ConflictingReport {
  /** The disagreeing source's id (e.g. 'healthmap', 'promed'). */
  source: string;
  /** The disagreeing source's reported value. */
  value: number;
  /** That source's confidence tier. */
  confidence: Confidence;
  /** That source's as-of timestamp. */
  asOf: string;
}

export interface Signal {
  /** Pathogen catalog id, or 'unverified-novel' for an unrecognised signal. */
  pathogenId: string;
  /** Optional sub-identity within a pathogen — e.g. a variant/lineage name
   *  ('JN.1', 'XFG.2'). Distinguishes per-lineage seq-frequency signals at the
   *  same place so fusion doesn't collapse them. */
  subtype?: string;
  location: SignalLocation;
  metric: SignalMetric;
  value: number;
  /** ISO timestamp the datum is *true as-of* (not when fetched). */
  asOf: string;
  /** Source adapter id, e.g. 'who-don' | 'healthmap' | 'cdc-nwss'. */
  source: string;
  confidence: Confidence;
  verificationStatus: VerificationStatus;
  denominator?: { population?: number; testsPerformed?: number };
  /** Source-specific display extras — NOT used in fusion or validation.
   *  e.g. WHO DON: suspected-case + death counts + outbreak title alongside
   *  the primary confirmed-case value.
   *
   *  `conflictingReports` is populated by `fuseSignals` when ≥2 sources report
   *  DISAGREEING numeric counts for the same event/place/metric: the primary
   *  keeps the highest-confidence (recency-tiebroken) value, the losers land
   *  here so surfaces can flag "sources disagree: N vs M". */
  context?: {
    suspectedCases?: number;
    deaths?: number;
    title?: string;
    conflictingReports?: ConflictingReport[];
  };
  provenance: SignalProvenance[];
  /** Optional time-series back-track for this same (pathogen, location, metric).
   *  Adapters that internally pull multiple weeks (RKI / Sentinelles / ONS /
   *  FluNet / NWSS, etc.) populate this with the most-recent N points so
   *  surfaces can render trend sparklines without a separate snapshot store.
   *  The CURRENT observation (`value` + `asOf`) is the latest of the series;
   *  history is sorted oldest → newest, length 1..~20. Omitted when the
   *  source only carries a single observation. */
  history?: Array<{ asOf: string; value: number }>;
  /** Geographic resolution of this signal — keyed to MAP BEHAVIOUR, not GIS
   *  taxonomy, so the enum stays stable as new feeds land:
   *    point        — has a real discrete coordinate (a town, a wastewater
   *                   catchment site, an outbreak location). Plot at lat/lon
   *                   always, never roll up.
   *    area         — an administrative region (state/province/sentinel
   *                   network by country). Plot at the area's centroid;
   *                   ELIGIBLE for the R2 sub-national rollup at low zoom.
   *    national     — a country-level aggregate. Plot at the country
   *                   centroid; NEVER rolls up (it IS the aggregate; double-
   *                   counting it into the rollup donut would inflate the
   *                   sub-national tier mix).
   *    nonspatial   — explicitly cross-boundary / not pinned to one
   *                   geography (e.g. NT bulletin's "73 measles cases across
   *                   Australia"). Table-only, NEVER on the map.
   *    unknown      — explicit fallback for cases that don't fit; signals
   *                   that need triage / adapter follow-up.
   *  Undefined → OutbreakMap falls back to an inferred resolution + emits a
   *  dev-time warning the first time inference fires per signal — the
   *  inference is a transitional scaffold, not the permanent rule. Adapters
   *  should set this explicitly. */
  geoResolution?: 'point' | 'area' | 'national' | 'nonspatial' | 'unknown';
}

/** Confidence ordering (higher = more authoritative). */
export const CONFIDENCE_RANK: Record<Confidence, number> = {
  'syndromic': 0,
  'automated-event': 1,
  'moderated-event': 2,
  'national-dashboard': 3,
  'official': 4,
};

/** Sources whose corroboration promotes a signal to 'confirmed'. */
const AUTHORITATIVE: ReadonlySet<Confidence> = new Set(['official', 'national-dashboard']);

// ---------------------------------------------------------------------------
// Validation (verifier-first)
// ---------------------------------------------------------------------------

export type SignalValidation =
  | { valid: true }
  | { valid: false; errors: string[] };

const METRICS: ReadonlySet<string> = new Set<SignalMetric>(
  ['cases', 'deaths', 'hospitalisations', 'rna-copies', 'seq-frequency', 'wastewater-activity', 'event-notification',
   'sequences', 'test-positivity', 'syndromic-rate', 'hospitalisation-rate']);

function isIso(s: unknown): boolean {
  return typeof s === 'string' && !Number.isNaN(Date.parse(s));
}

export function validateSignal(s: unknown): SignalValidation {
  const e: string[] = [];
  if (typeof s !== 'object' || s === null) return { valid: false, errors: ['signal must be an object'] };
  const r = s as Record<string, unknown>;
  if (typeof r.pathogenId !== 'string' || r.pathogenId.length === 0) e.push('pathogenId required');
  if (!METRICS.has(r.metric as string)) e.push(`metric '${String(r.metric)}' not in vocabulary`);
  if (typeof r.value !== 'number' || !Number.isFinite(r.value) || r.value < 0) e.push('value must be a finite number ≥ 0');
  if (!isIso(r.asOf)) e.push('asOf must be an ISO timestamp');
  if (typeof r.source !== 'string' || r.source.length === 0) e.push('source required (verifier-first: no anonymous data)');
  if (!(r.confidence as string in CONFIDENCE_RANK)) e.push(`confidence '${String(r.confidence)}' not in vocabulary`);
  if (!['unverified', 'probable', 'confirmed'].includes(r.verificationStatus as string)) e.push('verificationStatus invalid');
  const loc = r.location as Record<string, unknown> | undefined;
  if (!loc || typeof loc.lat !== 'number' || typeof loc.lon !== 'number' || typeof loc.admin !== 'string') {
    e.push('location {lat,lon,admin} required');
  }
  if (!Array.isArray(r.provenance) || r.provenance.length === 0) {
    e.push('provenance must be a non-empty array (verifier-first: every datum is traceable)');
  } else if (!(r.provenance as unknown[]).every(p => isIso((p as Record<string, unknown>)?.retrievedAt))) {
    e.push('each provenance entry needs an ISO retrievedAt');
  }
  const ctx = r.context as Record<string, unknown> | undefined;
  if (ctx?.conflictingReports !== undefined) {
    if (!Array.isArray(ctx.conflictingReports)) {
      e.push('context.conflictingReports must be an array');
    } else if (!(ctx.conflictingReports as unknown[]).every(c => {
      const cr = c as Record<string, unknown>;
      return cr && typeof cr.source === 'string' && cr.source.length > 0
        && typeof cr.value === 'number' && Number.isFinite(cr.value) && cr.value >= 0
        && (cr.confidence as string) in CONFIDENCE_RANK
        && isIso(cr.asOf);
    })) {
      e.push('each conflictingReports entry needs {source, value≥0, confidence, ISO asOf}');
    }
  }
  return e.length ? { valid: false, errors: e } : { valid: true };
}

// ---------------------------------------------------------------------------
// Fusion — dedup multi-source reports + promote-on-corroboration
// ---------------------------------------------------------------------------

export interface FusedSignal extends Signal {
  /** All sources that reported this (pathogen, location, metric). */
  sources: string[];
  /** Number of distinct sources corroborating. */
  corroboration: number;
}

/** Bucket key: same pathogen + subtype + metric + ~same place (0.5° grid ≈ regional). */
function fusionKey(s: Signal): string {
  const gl = (x: number) => (Math.round(x * 2) / 2).toFixed(1);
  return `${s.pathogenId}|${s.subtype ?? ''}|${s.metric}|${gl(s.location.lat)}|${gl(s.location.lon)}`;
}

/** Count-bearing metrics where two sources reporting different numbers is a
 *  genuine data conflict (not a units mismatch like wastewater-activity or a
 *  fraction like seq-frequency, which are not comparable cross-source counts). */
const COUNT_METRICS: ReadonlySet<SignalMetric> = new Set<SignalMetric>(['cases', 'deaths', 'hospitalisations']);

/** Order signals strongest→weakest: confidence desc, then recency desc (newer
 *  wins ties), then a stable source-id tiebreak so fusion is deterministic. */
function byAuthority(a: Signal, b: Signal): number {
  return (CONFIDENCE_RANK[b.confidence] - CONFIDENCE_RANK[a.confidence])
    || (Date.parse(b.asOf) - Date.parse(a.asOf))
    || a.source.localeCompare(b.source);
}

/**
 * Fuse raw signals into one record per (pathogen, place, metric).
 *
 * Primary value = the highest-confidence source's value, ties broken by recency
 * (newer wins) then source id (stable). verificationStatus is promoted to
 * 'confirmed' when ≥1 authoritative source is present; multiple non-authoritative
 * sources → 'probable'; a lone non-authoritative source keeps its own status.
 *
 * CONFLICT HANDLING (E2.4): when ≥2 *distinct sources* report DISAGREEING
 * numeric counts (cases/deaths/hospitalisations) for the same bucket, the
 * primary keeps the winning source's value and the disagreeing reports are
 * retained on `context.conflictingReports` rather than silently dropped — so a
 * surface can flag "sources disagree: N vs M". Corroborating sources that agree
 * on the value are NOT recorded as conflicts.
 */
export function fuseSignals(signals: readonly Signal[]): FusedSignal[] {
  const groups = new Map<string, Signal[]>();
  for (const s of signals) {
    const k = fusionKey(s);
    (groups.get(k) ?? groups.set(k, []).get(k)!).push(s);
  }
  const out: FusedSignal[] = [];
  for (const group of groups.values()) {
    const ranked = group.slice().sort(byAuthority);
    const primary = ranked[0]!;
    const sources = [...new Set(group.map(g => g.source))];
    const hasAuthoritative = group.some(g => AUTHORITATIVE.has(g.confidence));
    let status: VerificationStatus = primary.verificationStatus;
    if (hasAuthoritative && sources.length > 1) status = 'confirmed';
    else if (hasAuthoritative) status = 'confirmed';
    else if (sources.length > 1) status = 'probable'; // multiple non-authoritative → upgraded from unverified

    // Detect cross-source numeric disagreement on count metrics. Walk the
    // ranked-but-primary signals; keep one entry per distinct source whose
    // value differs from the primary's (don't double-record the same source).
    let conflictingReports: ConflictingReport[] | undefined;
    if (COUNT_METRICS.has(primary.metric)) {
      const seen = new Set<string>([primary.source]);
      for (const g of ranked.slice(1)) {
        if (g.source === primary.source || seen.has(g.source)) continue;
        if (g.value !== primary.value) {
          (conflictingReports ??= []).push({
            source: g.source, value: g.value, confidence: g.confidence, asOf: g.asOf,
          });
          seen.add(g.source);
        }
      }
    }

    const mergedContext = (primary.context || conflictingReports)
      ? { ...primary.context, ...(conflictingReports ? { conflictingReports } : {}) }
      : undefined;

    out.push({
      ...primary,
      verificationStatus: status,
      sources,
      corroboration: sources.length,
      provenance: group.flatMap(g => g.provenance),
      ...(mergedContext ? { context: mergedContext } : {}),
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Freshness — per-source cadence + staleness
// ---------------------------------------------------------------------------

/** Native refresh cadence per source (ms). A datum older than ~2× its
 *  source cadence is considered stale and must be visibly flagged. */
export const SOURCE_CADENCE_MS: Record<string, number> = {
  'healthmap':         30 * 60_000,        // ~30 min event polling
  // NOTE — promed/ecdc/nextclade/gisaid entries removed 2026-06-01: they
  // were planned-but-never-wired source ids with no corresponding adapter
  // in ADAPTERS. Caught by registry-invariants.test.ts. Add them back here
  // ONLY when a real adapter constant for each lands in src/adapters/.
  'who-don':           7 * 24 * 3_600_000,  // event-driven, published irregularly — weekly freshness window
  'who-flunet':        7 * 24 * 3_600_000,  // GISRS weekly reporting (with a multi-week lag)
  'cdc-nndss':         7 * 24 * 3_600_000,  // MMWR weekly reporting (provisional, ~1-2 wk lag)
  'cdc-nwss':          7 * 24 * 3_600_000,  // WVAL weekly (week_end), with reporting lag
  // cov-spectrum-lapis cadence aligned to adapter.cadenceMs (1d, not 14d).
  // 2026-06-01 cleanup: LAPIS itself updates daily even though sequence
  // submission delay is 2-4 wk; per-signal freshness uses adapter cadence.
  'cov-spectrum-lapis': 24 * 3_600_000,    // daily LAPIS refresh
  'genspectrum':       14 * 24 * 3_600_000, // open LAPIS (flu/dengue genomes); INSDC submission lag ~2-4 wk
  'pathoplexus':       14 * 24 * 3_600_000, // open LAPIS (mpox/WNV/CCHF/Ebola genomes); submission lag ~2-4 wk
  'ukhsa':             7 * 24 * 3_600_000,  // UKHSA England dashboard — COVID daily, flu/RSV weekly
  'owid-mpox':         7 * 24 * 3_600_000,  // OWID/WHO global mpox — roughly weekly country updates
  'opendengue':        30 * 24 * 3_600_000, // OpenDengue — LAGGED reference (republished ~yearly); always reads stale by design
  'owid-polio':        365 * 24 * 3_600_000, // OWID polio — annual reference; data 1-3y lagged → reads stale
  'owid-cholera':      365 * 24 * 3_600_000, // OWID cholera — annual reference; data ~4y lagged → reads stale
  'paho-avian-flu':    7 * 24 * 3_600_000,   // PAHO Americas avian-flu outbreak events — animal-side, weekly cadence
  'ons-uk-mortality':  7 * 24 * 3_600_000,   // UK ONS weekly deaths (England + Wales), per region + cause
  'cbs-nl-mortality':  7 * 24 * 3_600_000,   // CBS Netherlands weekly all-cause deaths (national)
  'nsw-au-respiratory': 7 * 24 * 3_600_000,  // NSW Health (AU) weekly COVID + flu + RSV notifications (PDF-extracted)
  'vic-au-respiratory': 7 * 24 * 3_600_000,  // VIC Health (AU) weekly COVID + flu + RSV notifications (PDF-extracted)
  'qld-au-respiratory': 7 * 24 * 3_600_000,  // QLD Health (AU) weekly COVID + flu + RSV notifications (PDF-extracted, static URL)
  'wa-au-respiratory':  7 * 24 * 3_600_000,  // WA Health (AU) Virus WAtch weekly respiratory (PDF-extracted)
  'sa-au-respiratory':  7 * 24 * 3_600_000,  // SA Health (AU) respiratory dashboard JSON (COVID + flu + RSV + pertussis)
  'tas-au-respiratory': 7 * 24 * 3_600_000,  // TAS Health (AU) RespTas weekly PDF (COVID + flu + RSV)
  'act-au-respiratory': 7 * 24 * 3_600_000,  // ACT Health (AU) weekly PDF (COVID + flu + RSV)
  'nt-au-surveillance': 30 * 24 * 3_600_000, // NT Health (AU) monthly Surveillance Update (outbreak counts)
  // Phase 11b (2026-06-01): QLD open-data fallback — runs in PARALLEL with the
  // structurally-blocked qld-au-respiratory PDF adapter; fuseSignals dedupes
  // the COVID overlap when both produce signals for the same week.
  'qld-au-opendata-covid': 7 * 24 * 3_600_000, // QLD Health open-data COVID-19 line list (data.qld.gov.au CKAN; weekly)
  'rki-germany':       7 * 24 * 3_600_000,  // RKI Germany — weekly ARE + SARI
  'sentinelles-france': 7 * 24 * 3_600_000, // Réseau Sentinelles France — weekly ILI
  'phac-canada':       7 * 24 * 3_600_000,  // PHAC Canada FluWatch+ — weekly respiratory positivity
  'cdc-resp-net':      7 * 24 * 3_600_000,  // CDC RESP-NET — weekly hospitalisation rates
  'delphi-nssp':       7 * 24 * 3_600_000,  // Delphi NSSP ED-visit % — weekly
  'ecdc-erviss':       7 * 24 * 3_600_000,  // ECDC ERVISS sentinel positivity — weekly
  'cdc-h5-wastewater': 7 * 24 * 3_600_000,  // CDC H5 wastewater — ~weekly sampling
  // nextclade + gisaid orphans removed 2026-06-01 (no corresponding adapter
  // in ADAPTERS; planned-but-never-wired). Add back if/when a real adapter
  // for either lands in src/adapters/.
  'woah':              24 * 3_600_000,
  // --- Phase 3 wave (2026-05-30 → 31): AFRO + PAHO + SEARO + EMRO + WPRO + EURO additions
  'afro-nicd-sa-respiratory':         7 * 24 * 3_600_000,
  'afro-malawi-phim-idsr':            7 * 24 * 3_600_000,
  'afro-nigeria-ncdc-wer':            7 * 24 * 3_600_000,
  'brazil-infodengue':                7 * 24 * 3_600_000,
  'argentina-dengue':                 7 * 24 * 3_600_000,
  'costa-rica-dengue':                14 * 24 * 3_600_000,
  'searo-lk-wer':                     7 * 24 * 3_600_000,
  'searo-bd-dghs-dengue':             1 * 24 * 3_600_000,  // BD DGHS daily dengue
  // SEARO bulletin is biweekly per source rhythm; aligned to adapter.cadenceMs (2026-06-01)
  'searo-who-bulletin':               14 * 24 * 3_600_000,
  // Thai DDC dengue dashboard publishes monthly; aligned to adapter.cadenceMs (2026-06-01)
  'searo-th-dengue':                  30 * 24 * 3_600_000,
  'emro-influenza-weekly':            7 * 24 * 3_600_000,
  'pk-nih-idsr-weekly':               7 * 24 * 3_600_000,
  'who-cholera-multi-country-monthly': 30 * 24 * 3_600_000,
  'emr-polio-bulletin-weekly':        7 * 24 * 3_600_000,
  'afg-ewars-weekly':                 7 * 24 * 3_600_000,
  'wpro-respiratory-bulletin':        7 * 24 * 3_600_000,
  'wpro-dengue-biweekly':             14 * 24 * 3_600_000,
  'wpro-avian-influenza-weekly':      7 * 24 * 3_600_000,
  'hk-chp-notifiable-monthly':        30 * 24 * 3_600_000,
  'jihs-jp-notifiable':               7 * 24 * 3_600_000,
  // NZ ESR wastewater is biweekly; aligned to adapter.cadenceMs (2026-06-01)
  'nz-phf-wastewater':                14 * 24 * 3_600_000,
  // Taiwan CDC dengue dashboard updates daily; aligned to adapter.cadenceMs (2026-06-01)
  'tw-cdc-dengue':                    24 * 3_600_000,
  'eurostat-mortality':               7 * 24 * 3_600_000,
  'ru-fic-influenza':                 7 * 24 * 3_600_000,
  'pri-scotland':                     7 * 24 * 3_600_000,
  'bag-switzerland':                  7 * 24 * 3_600_000,
  // PHW Wales is biweekly per source; aligned to adapter.cadenceMs (2026-06-01)
  'phw-wales':                        14 * 24 * 3_600_000,
  // --- Phase 3b Tier-2 wave (2026-05-31): EURO additional adapters
  'ecdc-respiratory-github':          7 * 24 * 3_600_000,
  'hpsc-ireland':                     7 * 24 * 3_600_000,
  'fohm-sweden':                      7 * 24 * 3_600_000,
  'spain-sivira':                     7 * 24 * 3_600_000,
  // --- Phase 3b Tier-2 wave (2026-05-31): AFRO additional adapters
  'afro-kenya-nphi-weekly':           7 * 24 * 3_600_000,
  'afro-ghana-ghs-gwer':              7 * 24 * 3_600_000,
  'afro-uganda-moh-weekly':           7 * 24 * 3_600_000,
  'afro-ethiopia-ephi-eweb':          7 * 24 * 3_600_000,
  // --- Phase 3b Tier-2 wave (2026-05-31): AMRO/PAHO LATAM additional adapters
  'chile-minsal-respiratory':         7 * 24 * 3_600_000,
  'mexico-dge-boletin':               7 * 24 * 3_600_000,
  'peru-minsa-cdc-respiratory':       7 * 24 * 3_600_000,
  'colombia-ins-sivigila':            7 * 24 * 3_600_000,
  // --- Phase 3b Tier-2 wave round 4 (2026-05-31): EMRO additional adapters (all BLOCKED)
  'saudi-moh-weekly':                 7 * 24 * 3_600_000,
  'uae-mohap-weekly':                 7 * 24 * 3_600_000,
  'egypt-mohp-weekly':                7 * 24 * 3_600_000,
  'israel-moh-respiratory':           7 * 24 * 3_600_000,
  // --- Phase 8.6 (2026-05-31): India IDSP — weekly NCDC outbreak bulletin
  'india-idsp-weekly':                7 * 24 * 3_600_000,
  // --- Phase 10.1d (2026-06-01): Iran MOHME — weekly disease bulletin
  // (BLOCKED: sanctions; adapter wire-ready pending OFAC + EMRO partner clearance)
  'iran-mohme-weekly':                7 * 24 * 3_600_000,
  // --- Phase 10.1b (2026-06-01): Vietnam MoH / NCDC — weekly notifiable bulletin
  'vietnam-moh-weekly':               7 * 24 * 3_600_000,
  // --- Phase 10.1c (2026-06-01): Philippines DOH — weekly bulletin
  'philippines-doh-weekly':           7 * 24 * 3_600_000,
  // --- Phase 10.1a (2026-06-01): Indonesia Kemenkes — weekly Situasi Epidemiologi
  'indonesia-kemenkes-weekly':        7 * 24 * 3_600_000,
  // --- Phase 10.2a (2026-06-01): China CCDC / NHC — MONTHLY notifiable bulletin
  // (NHC publishes monthly, NOT weekly — 30d cadence). Sovereignty caveat
  // documented in source-licenses.json (SARS-1 precedent + geo-fence risk).
  'china-ccdc-monthly':               30 * 24 * 3_600_000,
  // --- Phase 10.2b (2026-06-01): Russia Rospotrebnadzor — weekly federal-
  // subject disease surveillance (extends RuFicInfluenza to multi-disease IDSR)
  'russia-rospotrebnadzor-weekly':    7 * 24 * 3_600_000,
  // --- Phase 10.2c (2026-06-01): DRC MSPHP — weekly sitrep (~100M coverage at
  // province level; capacity-limit + conflict-zone caveats per adapter docblock)
  'drc-msphp-weekly':                 7 * 24 * 3_600_000,
  // --- Phase 10.2d (2026-06-01): South Korea KDCA — weekly notifiable-disease
  // (17 admin regions; KOGL Type 1 likely-permissive; MERS-CoV annual watch)
  'south-korea-kdca-weekly':          7 * 24 * 3_600_000,
};
/** Fallback cadence for any source not listed in SOURCE_CADENCE_MS (ms). */
export const DEFAULT_CADENCE_MS = 24 * 3_600_000;

export interface Freshness {
  ageMs: number;
  stale: boolean;
  cadenceMs: number;
}

/** Staleness of a signal given the current time. stale = age > 2× source cadence. */
export function signalFreshness(signal: Signal, nowMs: number = Date.now()): Freshness {
  const cadenceMs = SOURCE_CADENCE_MS[signal.source] ?? DEFAULT_CADENCE_MS;
  const ageMs = Math.max(0, nowMs - Date.parse(signal.asOf));
  return { ageMs, stale: ageMs > 2 * cadenceMs, cadenceMs };
}

// ---------------------------------------------------------------------------
// Feed health — per-source SLO surfacing (ingest status × signal freshness)
// ---------------------------------------------------------------------------

/**
 * Map an ingest SourceReport id to the `source` value its signals carry.
 * Most reports use the same id as their signals (e.g. 'cdc-nwss', 'who-don'),
 * but the variant feed is ingested per-country ('lapis:USA', 'lapis:Japan')
 * while every variant signal carries the single source 'cov-spectrum-lapis'.
 * Resolving here lets us attribute signals to a report without double-counting.
 */
export function reportSignalSource(reportId: string): string {
  if (reportId === 'lapis' || reportId.startsWith('lapis:')) return 'cov-spectrum-lapis';
  // Genomic feeds are ingested per-organism ('genspectrum:h3n2', 'pathoplexus:mpox')
  // but every signal carries the single platform source id.
  if (reportId === 'genspectrum' || reportId.startsWith('genspectrum:')) return 'genspectrum';
  if (reportId === 'pathoplexus' || reportId.startsWith('pathoplexus:')) return 'pathoplexus';
  return reportId;
}

/** Per-source feed health: ingest status fused with live-signal freshness. */
/** Why a BLOCKED source is structurally unavailable. Per CD R8 spec — the
 *  "permanence" attribute that distinguishes a permanent gap from a transient
 *  failure. Used by FeedAdapter.blockedReason + by §7.22 Feed Health to
 *  surface the right operator action + the right workaround copy. */
export type BlockedReason = 'antibot' | 'partnership' | 'sanctions' | 'geo';

export interface SourceHealth {
  /** Resolved signal-source id (e.g. 'cdc-nwss', 'who-don', 'cov-spectrum-lapis'). */
  id: string;
  /** Did every ingest task for this source succeed this run? */
  ok: boolean;
  /** First error message among the source's ingest tasks (if any failed). */
  error?: string;
  /** Number of fused signals currently attributed to this source. */
  signalCount: number;
  /** ISO `asOf` of the freshest signal from this source, or null if none. */
  latestAsOf: string | null;
  /** True when the latest signal is older than 2× this source's cadence —
   *  per-signal demotion rule; individual data points may be stale even when
   *  the source itself is still publishing on schedule. */
  stale: boolean;
  /** True when the latest signal is older than 4× this source's cadence —
   *  the SOURCE itself has stopped publishing (vs. individual signals
   *  aging out). At scale (100+ sources) the operator can't eyeball which
   *  sources rotted; this surfaces it explicitly so Feed Health can flag.
   *  Italy ISS suspended its data flow in March 2026 is the canonical
   *  example — was "stale" within days, "rotting" within ~2 months. */
  rotting: boolean;
  /** True when the source is STRUCTURALLY blocked (Cloudflare anti-bot,
   *  partnership-gated, sanctions, geo-firewall) — a permanent gap, not a
   *  transient failure. Per CD R8 § BLOCKED state: blocked sources must
   *  NEVER read identically to a 503/timeout (different operator action +
   *  different alert-rule treatment). Set declaratively on the FeedAdapter
   *  (blockedReason), NOT inferred from runtime failures. */
  blocked: boolean;
  /** Why blocked (when blocked=true). Drives the workaround copy in the
   *  Feed Health detail panel + the alert-rule BLOCKED-exclusion engine. */
  blockedReason?: BlockedReason;
  /** This source's freshness cadence (ms). */
  cadenceMs: number;
}

/**
 * Summarise per-source feed health from the live (fused) signals + the
 * ingest SourceReports. Pure + deterministic (pass nowMs). Reports that
 * share a signal source (the per-country variant tasks) are rolled up to a
 * single source row so signal attribution is exact (no double counting).
 *
 * `ok` is true only when every contributing ingest task succeeded; `error`
 * carries the first failure. `stale` reuses the signalFreshness 2×-cadence
 * rule against the freshest signal. A degraded source with no signals is
 * reported honestly (signalCount 0, latestAsOf null, stale false) — never
 * fabricated.
 */
export function sourceHealth(
  signals: readonly FusedSignal[],
  sources: readonly SourceReport[],
  nowMs: number = Date.now(),
  /** Map of source id → BlockedReason for STRUCTURALLY-blocked sources.
   *  Per CD R8: callers (the PaCC app) build this from the ADAPTERS map's
   *  declarative `blockedReason` fields and pass it in. Sources without
   *  an entry are treated as not-blocked; runtime failures fall through
   *  to DEGRADED. Optional so existing callers keep working. */
  blockedSources?: Readonly<Record<string, BlockedReason>>,
): SourceHealth[] {
  // Roll up reports that resolve to the same signal source.
  const reportsBySource = new Map<string, SourceReport[]>();
  for (const r of sources) {
    const key = reportSignalSource(r.id);
    (reportsBySource.get(key) ?? reportsBySource.set(key, []).get(key)!).push(r);
  }

  return [...reportsBySource.entries()].map(([id, reports]) => {
    const mine = signals.filter(s => s.sources.includes(id));
    let latestMs = -Infinity;
    let latestSignal: FusedSignal | undefined;
    for (const s of mine) {
      const ms = Date.parse(s.asOf);
      if (Number.isFinite(ms) && ms > latestMs) { latestMs = ms; latestSignal = s; }
    }
    const fresh = latestSignal ? signalFreshness(latestSignal, nowMs) : undefined;
    const failed = reports.find(r => !r.ok);
    const cadenceMs = fresh ? fresh.cadenceMs : (SOURCE_CADENCE_MS[id] ?? DEFAULT_CADENCE_MS);
    // `rotting` is the source-level signal: the freshest datum is older than
    // 4× the source's cadence. At weekly cadence that's ~1 month; at monthly
    // ~4 months; at annual ~4 years. Different from `stale` (per-signal,
    // 2× cadence) — rotting means the SOURCE has likely stopped publishing.
    const rotting = fresh ? fresh.ageMs > 4 * cadenceMs : false;
    const blockedReason = blockedSources?.[id];
    return {
      id,
      ok: reports.every(r => r.ok),
      ...(failed?.error != null ? { error: failed.error } : {}),
      signalCount: mine.length,
      latestAsOf: latestSignal ? latestSignal.asOf : null,
      stale: fresh ? fresh.stale : false,
      rotting,
      blocked: blockedReason != null,
      ...(blockedReason != null ? { blockedReason } : {}),
      cadenceMs,
    };
  });
}

// ---------------------------------------------------------------------------
// Per-capita denominators (E2.3) — resolve a signal's population denominator
// so counts can be shown per-100k. Pure + offline-testable: the caller passes
// a minimal denominator table (built from the region catalog) so this package
// keeps zero dependency on plugin-region-database.
// ---------------------------------------------------------------------------

/** Minimal population-bearing region descriptor (subset of a region record). */
export interface PopulationDenominator {
  /** ISO-3 country code, upper-case (e.g. 'USA'). */
  iso3?: string;
  /** English region name, for admin-string matching when no iso3 is present. */
  name: string;
  /** Total population. */
  population: number;
  /** Region centroid [lat, lon], for nearest-centroid fallback matching. */
  centroidLatLon?: [number, number];
}

export type DenominatorMatch = 'iso3' | 'name' | 'centroid';

export interface ResolvedDenominator {
  population: number;
  /** How the signal was matched to this denominator (for provenance/UI note). */
  matchedBy: DenominatorMatch;
}

/** Great-circle-ish squared degree distance (cheap; only used for ranking). */
function degDist2(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const dLat = aLat - bLat;
  const dLon = (aLon - bLon) * Math.cos((aLat + bLat) / 2 * Math.PI / 180);
  return dLat * dLat + dLon * dLon;
}

/** Max centroid distance (degrees) we'll accept for a fallback match — beyond
 *  ~5° (~550 km) the "nearest region" is almost certainly the wrong country,
 *  so we'd rather report "no denominator" than divide by the wrong population. */
const MAX_CENTROID_MATCH_DEG = 5;

/**
 * Resolve a signal to a population denominator, trying (in order):
 *   1. exact iso3 match (signal.location.iso3 → region.iso3);
 *   2. case-insensitive admin/name match (signal.location.admin contains, or
 *      equals, a region name) — handles WHO DON country-name signals;
 *   3. nearest region centroid within MAX_CENTROID_MATCH_DEG.
 * Returns null when nothing matches confidently — callers MUST then show the
 * absolute count + a "no denominator" note rather than a wrong per-capita value.
 */
export function resolveDenominator(
  signal: Pick<Signal, 'location'>,
  denominators: readonly PopulationDenominator[],
): ResolvedDenominator | null {
  const iso = signal.location.iso3?.toUpperCase();
  if (iso) {
    const hit = denominators.find(d => d.iso3?.toUpperCase() === iso);
    if (hit && hit.population > 0) return { population: hit.population, matchedBy: 'iso3' };
  }
  const admin = signal.location.admin?.trim().toLowerCase();
  if (admin) {
    // Prefer exact name equality, then a contains match (e.g. 'measles · Yemen').
    const exact = denominators.find(d => d.name.trim().toLowerCase() === admin);
    const named = exact ?? denominators.find(d => {
      const n = d.name.trim().toLowerCase();
      return n.length > 0 && admin.includes(n);
    });
    if (named && named.population > 0) return { population: named.population, matchedBy: 'name' };
  }
  // Nearest-centroid fallback (bounded). Only regions with a centroid + pop.
  let best: PopulationDenominator | undefined;
  let bestD = Infinity;
  for (const d of denominators) {
    if (!d.centroidLatLon || d.population <= 0) continue;
    const dist = degDist2(signal.location.lat, signal.location.lon, d.centroidLatLon[0], d.centroidLatLon[1]);
    if (dist < bestD) { bestD = dist; best = d; }
  }
  if (best && bestD <= MAX_CENTROID_MATCH_DEG * MAX_CENTROID_MATCH_DEG) {
    return { population: best.population, matchedBy: 'centroid' };
  }
  return null;
}

/** Cases (or any count) per 100,000 population. Returns null for a non-positive
 *  population (no fabricated rate). Rounded to 2 dp for display stability. */
export function perCapita100k(value: number, population: number): number | null {
  if (!(population > 0) || !Number.isFinite(value)) return null;
  return Math.round((value / population) * 100_000 * 100) / 100;
}

// ---------------------------------------------------------------------------
// Adapter contract
// ---------------------------------------------------------------------------

export interface FeedAdapter<Raw = unknown> {
  /** Stable source id; should match a SOURCE_CADENCE_MS key. */
  id: string;
  /** Human label. */
  label: string;
  cadenceMs: number;
  /** True when this adapter publishes LAGGED REFERENCE data (annual cholera /
   *  polio totals, OpenDengue's ~1-yr-lagged national extract). The lag is
   *  the source's published cadence, not a malfunction. The pre-wire QA
   *  verifier exempts these from the cadence × 3 freshness ceiling; the
   *  threat-tier engine already demotes their signals to ROUTINE via the
   *  `stale` flag, which is the correct user-visible behaviour. Omitting
   *  the field (default false) means "current data" and the freshness gate
   *  applies normally. */
  referenceOnly?: boolean;
  /** When set, the source is STRUCTURALLY blocked (Cloudflare anti-bot,
   *  partnership-gated, sanctions, geo-firewall). Per CD R8 § BLOCKED state:
   *  blocked sources need a different operator action from transient 503s,
   *  must be excluded from "any source DEGRADED" alert rules (avoid
   *  permanent alert fatigue), and earn an explicit "workaround" panel
   *  in §7.22 Feed Health. Set DECLARATIVELY here, not inferred from
   *  runtime failures — the adapter knows it's blocked, the engine
   *  doesn't have to guess. Omitting the field (default undefined) means
   *  "not blocked"; runtime failures fall through to DEGRADED. */
  blockedReason?: BlockedReason;
  /** Pure: raw source payload → Signal[]. No network here (testable offline). */
  normalise(raw: Raw, retrievedAt: string): Signal[];
}

// ---------------------------------------------------------------------------
// In-memory injectable store (browser-safe; mirrors the catalog injection
// pattern — an adapter/runtime calls __setSignalRecords, surfaces read).
// ---------------------------------------------------------------------------

let REGISTRY: FusedSignal[] = [];

/** Replace the live-signal registry with a freshly-fused snapshot. */
export function __setSignalRecords(signals: readonly Signal[]): void {
  for (const s of signals) {
    const v = validateSignal(s);
    if (!v.valid) throw new Error(`__setSignalRecords: invalid signal — ${v.errors.join('; ')}`);
  }
  REGISTRY = fuseSignals(signals);
}

export function listSignals(): readonly FusedSignal[] { return REGISTRY; }

export function getSignalsByPathogen(pathogenId: string): FusedSignal[] {
  return REGISTRY.filter(s => s.pathogenId === pathogenId);
}

export function getConfirmedSignals(): FusedSignal[] {
  return REGISTRY.filter(s => s.verificationStatus === 'confirmed');
}

// Types from ingest (erased at runtime — safe for the browser bundle).
// The RUNTIME `ingestAll` + `DEFAULT_INGEST_CONFIG` are NOT re-exported here
// because the ingest module pulls in adapters/index.ts → fetch-cache.ts →
// node:crypto/node:fs, which Vite externalises and the browser then throws on.
// Node-only consumers (ingest script, tests) import from the './ingest'
// subpath: `import { ingestAll } from '@biomax/plugin-surveillance-feed/ingest'`.
export type {
  IngestConfig, IngestDeps, IngestResult, SourceReport,
} from './ingest.js';

export const manifest: PluginManifest = {
  id: 'surveillance-feed', version: '0.1.0',
  engineCompat: '^0.5.0', kingdoms: '*', systems: [], components: [],
};

// Canonical country lookup — adapters should resolve source-specific country
// strings through this to avoid silently dropping data when names differ.
export { canonicalCountry, CANONICAL_COUNTRIES, type CanonicalCountry } from './canonicalCountry.js';

// Reporting-day bias indicator — flags when a signal's asOf falls on a
// weekend or curated public holiday so operators don't read a Sunday
// reporting gap as a real-world improvement. See docs/phase-7-governance/
// bias-audit.md § "Reporting-lag bias".
export {
  isReportingDayUnusual, reportingDayNote, SUPPORTED_HOLIDAY_JURISDICTIONS,
} from './holidays.js';

// Typed loaders for the data registers (license + population denominators).
// Browser-safe (plain JSON imports, no node:* touched). Pairs with the
// JSON files at `data/source-licenses.json` + `data/population-denominators.json`.
// The population register is GOVERNANCE-rich (verifyUrl, verifiedAt, notes);
// `toPopulationDenominators()` converts to the lean shape resolveDenominator()
// consumes, filtering out unverified entries (population: null) so unverified
// data can never silently produce wrong per-capita rates.
export {
  loadSourceLicenses, getLicense, getSourcesWithWarnings,
  loadPopulationDenominators, getPopulation, toPopulationDenominators,
  loadCoverageTier, listNonDisseminators, listBlockedAdapters,
  getBlockedAdapter, listLowYieldByDesign, listStagedAdapters, stagedSourceIds,
  type LicenseEntry, type PopulationEntry,
  type CoverageTierRegister, type NonDisseminator, type BlockedAdapterEntry,
  type LowYieldByDesignEntry, type ParallelFallback, type StagedAdapterEntry,
} from './registers.js';

// Phase 7.2 — per-source per-day history register. Each ingest run
// appends today's per-source state rollup; the §7.22 Feed Health
// SourcesView reads the last 30 days for the per-source history strip.
// Pure functions over a small JSON file (~2KB); app-side I/O.
export {
  appendDailyEntry, appendIngestResult, deriveSourceState,
  getSourceHistory, getHistoryCoverage, EMPTY_HISTORY,
  type SourceState, type DailySourceStates, type SourceHistory,
} from './history.js';

// Liveness harness — honest "how many sources are live RIGHT NOW" over
// sourceHealth(). Pure summary; the 6-hourly ingest cron persists it as a
// committed dated artifact (the track record) + surfaces the headline in
// §7.22 Feed Health. live = flowing fresh; aging = reachable-but-stale;
// down = failed/empty; blocked = staged (not a failure).
export {
  summariseLiveness, livenessHistoryLine,
  type LivenessStatus, type LivenessSource, type LivenessSnapshot,
} from './liveness.js';

// License-enforcement layer on top of registers.ts. Turns the JSON facts
// into product behaviour: paid-tier filtering (fail-closed for unverified),
// briefing-footer attribution collection, throwing assertions for export
// boundaries, register-wide readiness summary for the §7.22 Feed Health
// License-readiness surface.
export {
  getSourceClearance, isPaidTierCleared, assertCommercialUse,
  filterByLicense, getRequiredAttributions, summariseLicenseReadiness,
  LicenseViolationError,
  type PaidTierClearance, type LicenseMode, type AttributionLine,
  type LicenseReadiness,
} from './licenses.js';

// Note: the conditional-request fetch cache (./fetch-cache.ts) used to be
// re-exported here, but it imports node:crypto / node:fs — re-exporting from
// the package's main entry pulled them into the browser bundle via catalog.ts,
// and Vite externalises node:* (correctly) which then throws at runtime when
// adapter/index.ts code is referenced. Adapters import directly from
// '../fetch-cache.js' (server-only path); browser surfaces never need the
// cache helpers. If a future Node-only consumer wants them, import from
// '@biomax/plugin-surveillance-feed/fetch-cache' (would need a separate
// package export entry — currently inlined into adapters).
